/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { postsApi } from 'src/api/posts';
import { CrossIcon } from 'src/assets/icons/challengePage/cross';
import { CircleBrandingScope, useBranding } from 'src/branding';
import { MaterialCircularProgress } from 'src/components/material/CircularProgress';
import ScrollContainer from 'src/pages/FeedPage/VideoList/VideoList';

import styles from './ChallengePage.module.scss';

export const ChallengePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [posts, setPosts] = useState<any[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [ready, setReady] = useState(false);

  const urlParts = location.pathname.split('/');
  const circleId = urlParts[2];
  const challengeId = urlParts[4];

  // The challenge viewer belongs to the circle, so it follows its branding
  const branding = useBranding(circleId);

  const initialChallengeId = useRef(challengeId);
  const activePostRef = useRef<string | null>(null);

  // Fetch posts for the circle
  const fetchCirclePosts = async (page: number) => {
    try {
      setLoadingPosts(true);
      const response = await postsApi.getPostsForCircle(circleId, page);
      const { result, pagination } = response;

      setPosts((prevPosts) =>
        page === 1 ? result : [...prevPosts, ...result]
      );
      setTotalPages(pagination.totalPages ?? 1);
      setCurrentPage(page);
      return result;
    } catch (error) {
      console.error('Failed to fetch posts:', error);
      return [];
    } finally {
      setLoadingPosts(false);
    }
  };

  // Immediately show the target post, then load the full list in the background
  useEffect(() => {
    const abortController = new AbortController();
    const { signal } = abortController;
    const targetId = initialChallengeId.current;

    const load = async () => {
      // Step 1: Fetch the clicked post immediately so user sees it with no delay
      try {
        const targetPost = await postsApi.getSelectedPost(targetId, signal);
        if (signal.aborted) {
          return;
        }
        setPosts([targetPost]);
      } catch {
        if (signal.aborted) {
          return;
        }
        // Target post not found — fall through to paginated load
      }

      // Step 2: Load pages in the background until the target is in the list
      let page = 1;
      let allPosts: any[] = [];
      let pages = 1;

      while (!signal.aborted) {
        try {
          const response = await postsApi.getPostsForCircle(
            circleId,
            page,
            signal
          );
          const { result, pagination } = response;
          allPosts = page === 1 ? result : [...allPosts, ...result];
          pages = pagination.totalPages ?? 1;

          if (signal.aborted) {
            return;
          }

          const found = allPosts.some((p) => p._id === targetId);
          if (found || page >= pages) {
            setPosts(allPosts);
            setTotalPages(pages);
            setCurrentPage(page);
            break;
          }
          page++;
        } catch (error) {
          if (signal.aborted) {
            return;
          }
          console.error('Failed to fetch posts:', error);
          break;
        }
      }

      if (!signal.aborted) {
        setReady(true);
      }
    };

    load();
    return () => {
      abortController.abort();
    };
  }, [circleId]);

  const handleLoadMore = useCallback(
    (page: number) => {
      if (!loadingPosts) {
        fetchCirclePosts(page);
      }
    },
    [loadingPosts, circleId]
  );

  const handleActivePostChange = useCallback(
    (post: any) => {
      if (ready && post && post._id !== activePostRef.current) {
        activePostRef.current = post._id;
        navigate(`/circle/${circleId}/challenge/${post._id}`, {
          replace: true,
        });
      }
    },
    [circleId, navigate, ready]
  );

  if (!ready && posts.length === 0) {
    return (
      <CircleBrandingScope branding={branding}>
        <div className={styles.loaderContainer}>
          <MaterialCircularProgress />
        </div>
      </CircleBrandingScope>
    );
  }

  if (!posts.length) {
    return (
      <CircleBrandingScope branding={branding}>
        <div className={styles.loaderContainer}>No videos available</div>
      </CircleBrandingScope>
    );
  }

  return (
    <CircleBrandingScope branding={branding}>
      <div className={styles.challengeScroll}>
        <button
          className={styles.closeButton}
          onClick={() => navigate(`/circle/${circleId}`)}
        >
          <CrossIcon />
        </button>
        <ScrollContainer
          posts={posts}
          loadMorePosts={handleLoadMore}
          isLoading={loadingPosts}
          hasMorePosts={currentPage < totalPages}
          currentPage={currentPage}
          initialPostId={initialChallengeId.current}
          onActivePostChange={handleActivePostChange}
        />
      </div>
    </CircleBrandingScope>
  );
};
